import { FC, useMemo } from 'react';
import { z } from 'zod';
import { CatalogEntryIARecommendation } from '@/components/content/CatalogEntryIARecommendation';
import { ProductType } from '@/data/types/Product';
import { CLICK_PRODUCT_VIEW_SOURCE } from '@/data/constants/clickProductView';
import { partNumbersIARecommendationExtractorByUnknown } from '@/utils/partNumbersIARecommendationExtractorByUnknown';

const paramsSchema = z.object({
	partNumber: z.string(),
});

const transformSchema = z
	.object({
		order: z.enum([
			'similarity',
			'ascending-price',
			'descending-price',
			'ascending-offer',
			'descending-offer',
		]),
	})
	.optional();

const getParamsFromUnknown = (rawParams: unknown) => {
	try {
		const parsed = paramsSchema.safeParse(rawParams);
		if (!parsed.success) {
			return null;
		}

		return parsed.data;
	} catch (error) {
		console.error(error);
		return null;
	}
};

const getTransformFromUnknown = (rawTransform: unknown) => {
	try {
		const parsed = transformSchema.safeParse(rawTransform);

		if (!parsed.success) {
			return null;
		}

		return parsed.data;
	} catch (error) {
		console.error(error);
		return null;
	}
};

const filtersSchema = z
	.array(
		z.object({
			name: z.string(),
			operation: z.union([z.string(), z.record(z.unknown())]),
		})
	)
	.optional();

const getFiltersFromUnknown = (rawFilters: unknown) => {
	try {
		const parsed = filtersSchema.safeParse(rawFilters);
		if (!parsed.success) {
			return null;
		}
		return parsed.data ?? [];
	} catch (error) {
		console.error(error);
		return null;
	}
};

const emptyExtractor = (products: ProductType[], type: 'recommendation' | 'outfit') => {
	if (type === 'recommendation') {
		return products.length === 0;
	}

	return products.length === 0 || products.length < 4;
};

export const DpCarouselIARecommendation: FC<{
	id: string;
	title?: string;
	params: unknown;
	transform: unknown;
	filters: unknown;
	type?: 'recommendation' | 'outfit';
	showRecommendationBadge?: boolean;
	highlightFirstProduct?: boolean;
	clickSource?: CLICK_PRODUCT_VIEW_SOURCE;
}> = ({
	id,
	title,
	params: rawParams,
	transform: rawTransform,
	type = 'recommendation',
	highlightFirstProduct,
	showRecommendationBadge,
	clickSource,
	filters: rawFilters,
}) => {
	const params = useMemo(() => {
		const params = getParamsFromUnknown(rawParams);
		return params;
	}, [rawParams]);

	const transform = useMemo(() => {
		const transform = getTransformFromUnknown(rawTransform);
		return transform;
	}, [rawParams]);

	const filters = useMemo(() => {
		const baseFilters = getFiltersFromUnknown(rawFilters) ?? [];
		return baseFilters;
	}, [rawFilters, params]);

	if (!params) {
		return null;
	}

	return (
		<CatalogEntryIARecommendation
			id={id}
			title={title}
			params={params}
			transform={transform}
			highlightFirstProduct={highlightFirstProduct}
			showRecommendationBadge={showRecommendationBadge}
			clickSource={clickSource}
			filters={filters}
			partNumbersExtractor={(data) => partNumbersIARecommendationExtractorByUnknown(type, data)}
			emptyExtractor={(data) => emptyExtractor(data, type)}
		/>
	);
};
